<template>
  <VCard class="pa-4 add-processing pt-sm-4 pt-12 wishlist-create">
    <SLoadingOverlay :loading="loading" />
    <VCardTitle
      class="text-uppercase font-weight-bold pa-0 pl-4"
      :class="{ 'pb-6' : !wishlist}"
    >
      <VRow no-gutters>
        <VCol v-if="!wishlist" cols="11">
          <h4>
            {{ $t('wishlist.title.create') }}
          </h4>
        </VCol>
        <VCol cols="1" class="text-right ml-auto">
          <SBtn
            color="primary"
            class="mr-n3"
            text
            data-testid="close"
            @click.stop.prevent="close"
          >
            <SIcon :icon="icons.mdiClose" :label="$t('dialog.close.aria-label')" />
          </SBtn>
        </VCol>
      </VRow>
    </VCardTitle>
    <ObservedForm
      ref="listNameRef"
      data-testid="wishlist-edit-form"
      @submit="submit"
    >
      <VCardText class="py-0">
        <TextFieldWithValidation
          v-model="name"
          :label="$t('label.name')"
          rules="required|max:50"
          name="name"
          data-focus-first
          data-testid="list-name"
          required
          @click.stop.prevent
        />
        <ErrorContainer
          v-if="editError"
          :message="editError"
          class="pa-0 ma-0 font12"
          data-testid="wishlist-edit-error"
          no-border
          xs
        />
      </VCardText>
      <VCardActions class="text-center py-0 px-1">
        <VContainer>
          <VRow class="py-1">
            <VCol cols="12">
              <SBtn
                action
                color="primary"
                block
                type="submit"
                data-testid="list-create"
                @click.stop
              >
                {{ saveBtnLabel }}
              </SBtn>
            </VCol>
          </VRow>
          <VRow>
            <VCol cols="12" class="py-0">
              <SBtn
                class="text-underline text-uppercase"
                link-small
                data-testid="cancel"
                @click.stop.prevent="cancel"
              >
                {{ $t('input.btn.cancel') }}
              </SBtn>
            </VCol>
          </VRow>
        </VContainer>
      </VCardActions>
    </ObservedForm>
  </VCard>
</template>

<script setup lang="ts">
import { mdiClose } from '@mdi/js'
import TextFieldWithValidation from '~/components/base/fields/TextFieldWithValidation.vue'
import ObservedForm from '~/components/base/ObservedForm.vue'
import ErrorContainer from '~/components/base/ErrorContainer.vue'
import type { Wishlist } from '~/types/wishlist'
import { useAction } from '~/composables/action'
import { useWishlistStore } from '~/stores/wishlist'

interface Props {
  wishlist?: Wishlist
}

const props = withDefaults(defineProps<Props>(), {
  wishlist: undefined
})
const emits = defineEmits(['created', 'cancel', 'close'])

const icons = { mdiClose }
const editError = ref('')
const name = ref('')
const listNameRef = useTemplateRef('listNameRef')

const wishlistStore = useWishlistStore()
const createList = async () => {
  await wishlistStore.createList({ name: name.value })
}

watch(name, () => {
  editError.value = ''
})

const { loading, processAction } = useAction()
const { backendMessage } = useErrorText()

const submit = () => {
  processAction(async () => {
    try {
      if (props.wishlist) {
        await wishlistStore.renameList({ newName: name.value, id: props.wishlist.id })
      } else {
        await createList()
      }
      reset()
      emits('created')
    } catch (e) {
      editError.value = backendMessage(e)
    }
  })
}

const i18n = useI18n()

const saveBtnLabel = computed(() => {
  return props.wishlist ? i18n.t('input.btn.saveChanges') : i18n.t('wishlist.title.create')
})

onMounted(() => {
  if (props.wishlist) {
    name.value = props.wishlist.name
  }
})

const cancel = () => {
  reset()
  emits('cancel')
}

const close = () => {
  reset()
  emits('close')
}

const reset = () => {
  name.value = ''
  listNameRef.value?.resetForm()
}
</script>

<style lang="scss" scoped>
</style>
